import { Ref, useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { MapProps, MapRef } from 'react-map-gl';
import { CONSTANTS } from '../constants';
import { getHybridMapStyles } from '../utils/hybridMap.style';
import { getStandardMapStyles } from '../utils/standardMap.style';
import mapboxgl from 'mapbox-gl';
import { OutageLngLat } from '../types';
import config from '../../../config';
import { useIsMobile } from '../../../util/style-utils';
import {
  MapType,
  useOutageMapOptions,
} from '../providers/OutageMapOptionsProvider';
import { getMonochromeStyle } from '../utils/monochromeMap.style';
import { getSatelliteMapStyles } from '../utils/satelliteMap.style';
import { usePspsArcgisData } from './usePspsArcgisData';
import { FeatureCollection } from 'geojson';

const apiKey = config.amazonLocationService.key;
const region = config.amazonLocationService.region;

export const getDefaultMapStyle = () => getStandardMapStyles(apiKey, region);

export interface ALSMapProperties {
  isLoaded: boolean;
  mapOptions: MapProps & { ref: Ref<MapRef> };
  mapInstance: mapboxgl.Map | null;
  resetView: () => void;
  fitBounds: (points: OutageLngLat[]) => void;
  pspsGeoJsonData: FeatureCollection | null;
}

const useOutageMap = (): ALSMapProperties => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [mapInstance, setMapInstance] = useState<mapboxgl.Map | null>(null);
  const mapRef = useRef<MapRef>(null);
  const isMobile = useIsMobile();
  const { mapStyle } = useOutageMapOptions();
  const { pspsGeoJsonData } = usePspsArcgisData({ shouldFetch: true });

  const handleLoad = useCallback(() => {
    const map = mapRef.current?.getMap();
    if (map) {
      setMapInstance(map);
      setIsLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (!mapInstance) {
      return;
    }
    mapInstance.setMinZoom(
      isMobile
        ? CONSTANTS.MAP_CONFIG.minZoomMobile
        : CONSTANTS.MAP_CONFIG.minZoom,
    );
    mapInstance.setMaxBounds(
      isMobile
        ? CONSTANTS.MAP_CONFIG.maxBoundsCoordinatesMobile
        : CONSTANTS.MAP_CONFIG.maxBoundsCoordinates,
    );
    mapInstance.touchZoomRotate.disableRotation();
  }, [isMobile, mapInstance]);

  const fitBounds = useCallback(
    (points: OutageLngLat[] = []) => {
      if (!mapInstance || !points.length) {
        return;
      }
      const bounds = new mapboxgl.LngLatBounds();
      points.forEach(p => bounds.extend([p.lng, p.lat]));
      mapInstance.fitBounds(bounds, { padding: 150, duration: 0 });
    },
    [mapInstance],
  );

  const resetView = useCallback(() => {
    if (!mapInstance) {
      return;
    }
    mapInstance.flyTo({
      center: [
        CONSTANTS.MAP_CONFIG.defaultLng,
        CONSTANTS.MAP_CONFIG.defaultLat,
      ],
      zoom: isMobile
        ? CONSTANTS.MAP_CONFIG.minZoomMobile
        : CONSTANTS.MAP_CONFIG.minZoom,
      duration: 0,
    });
  }, [mapInstance]);

  const mapStyleDescriptor = useMemo(() => {
    if (mapStyle === MapType.MONOCHROME) {
      return getMonochromeStyle(
        apiKey,
        region,
      ) as import('@maplibre/maplibre-gl-style-spec').StyleSpecification;
    } else if (mapStyle === MapType.HYBRID) {
      return getHybridMapStyles(
        apiKey,
        region,
      ) as import('@maplibre/maplibre-gl-style-spec').StyleSpecification;
    } else if (mapStyle === MapType.SATELLITE) {
      return getSatelliteMapStyles(
        apiKey,
        region,
      ) as import('@maplibre/maplibre-gl-style-spec').StyleSpecification;
    } else {
      return getStandardMapStyles(
        apiKey,
        region,
      ) as import('@maplibre/maplibre-gl-style-spec').StyleSpecification;
    }
  }, [mapStyle]);

  return {
    mapInstance,
    isLoaded,
    mapOptions: {
      initialViewState: {
        longitude: CONSTANTS.MAP_CONFIG.defaultLng,
        latitude: CONSTANTS.MAP_CONFIG.defaultLat,
        zoom: CONSTANTS.MAP_CONFIG.defaultZoom,
      },
      mapStyle: mapStyleDescriptor,
      maxZoom: CONSTANTS.MAP_CONFIG.maxZoom,
      scrollZoom: true,
      dragRotate: false,
      attributionControl: false,
      ref: mapRef,
      onLoad: handleLoad,
    },
    pspsGeoJsonData,
    fitBounds,
    resetView,
  };
};

export default useOutageMap;
